
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { MAX_INDIVIDUAL_LOCATIONS } from '@/utils/constants';
import TextField from '@/components/shared/TextField.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';

@Component<LocationAssignments>({
	components: {
		TextField,
		InfoBanner
	}
})
export default class LocationAssignments extends Vue {
	@Prop({ type: Array, default: () => [] }) private assignments!: LocationAssignment[];
	@Watch('assignments')
	private onAssignmentsChange(): void {
		this.seedSelection();
		this.expandedGroupNames = this.groups.filter(group => this.isGroupExpandable(group)).map(group => group.name);
		this.$emit('change', this.payload);
	}

	private readonly maxIndividualLocations: number = MAX_INDIVIDUAL_LOCATIONS;
	private selectedLocationIds: string[] = [];
	private expandedGroupNames: string[] = [];
	private searchString: string = '';

	private get selectedLocationIdsSet(): Set<string> {
		return new Set(this.selectedLocationIds);
	}

	// Whether at least one non-disabled (editable) location is selected
	private get hasEditableSelection(): boolean {
		return this.selectedLocationIds.some(id => !this.disabledLocationIds.has(id));
	}

	private get groups(): UserLocationGroup[] {
		return this.assignments.filter((entry): entry is UserLocationGroup => entry.type === 'group');
	}

	private get allLocations(): UserLocation[] {
		const locations: UserLocation[] = [];
		this.assignments.forEach(entry => {
			if (entry.type === 'group') {
				locations.push(...entry.locations);
			}
			else {
				locations.push(entry);
			}
		});
		return locations;
	}

	private get locationNameById(): Map<string, string> {
		const names = new Map<string, string>();
		this.allLocations.forEach(location => names.set(location.id, location.name));
		return names;
	}

	// Out-of-scope locations the operator cannot select/unselect
	private get disabledLocationIds(): Set<string> {
		const ids = new Set<string>();
		this.allLocations.forEach(location => {
			if (!location.editable) {
				ids.add(location.id);
			}
		});
		return ids;
	}

	private get normalizedSearch(): string {
		return this.searchString.trim().toLowerCase();
	}

	// Locations and groups matching the search string
	private get visibleAssignments(): LocationAssignment[] {
		return this.assignments.filter(entry =>
			entry.type === 'group'
				? this.matchesSearch(entry.name) || entry.locations.some(location => this.matchesSearch(location.name))
				: this.matchesSearch(entry.name)
		);
	}

	// Turn the selected location IDs into a 'restaurants'/'groups' payload (format for Auth0 app_metadata)
	private get payload(): LocationAssignmentsPayload {
		const locationsGroupAlreadyAdded: Set<string> = new Set();
		const groups: string[] = [];
		let fallbackLocation: UserLocation | undefined;

		this.groups.forEach(group => {
			if (!group.locations.length) {
				return;
			}
			// If every location of a group is selected, store only the group
			if (group.locations.every(location => this.selectedLocationIdsSet.has(location.id))) {
				groups.push(group.name);
				group.locations.forEach(location => locationsGroupAlreadyAdded.add(location.id));
				fallbackLocation = fallbackLocation ?? group.locations[0];
			}
		});

		// Remaining locations (group couldn't be added, or group-less) are added to 'restaurants'
		const restaurants = this.selectedLocationIds
			.filter(id => !locationsGroupAlreadyAdded.has(id))
			.map(id => ({ id, name: this.locationNameById.get(id) ?? '' }));

		// If restaurants is empty, fill with one location from an assigned group
		if (!restaurants.length && fallbackLocation) {
			restaurants.push({ id: fallbackLocation.id, name: fallbackLocation.name });
		}

		return { groups, restaurants };
	}

	private created(): void {
		this.seedSelection();
		this.expandedGroupNames = this.groups.filter(group => this.isGroupExpandable(group)).map(group => group.name);
		this.$emit('change', this.payload);
	}

	/**
	 * Check a name matches the current search
	 *
	 * @param {string} name
	 * @return {boolean}
	 */
	private matchesSearch(name: string): boolean {
		return name.toLowerCase().includes(this.normalizedSearch);
	}

	/**
	 * List of locations within a group to display. If the group name matches the search (or no search), show all its locations.
	 * Else, show the locations whose name is matching the search.
	 *
	 * @param {UserLocationGroup} group
	 * @return {UserLocation[]}
	 */
	private visibleGroupLocations(group: UserLocationGroup): UserLocation[] {
		if (this.matchesSearch(group.name)) {
			return group.locations;
		}
		return group.locations.filter(location => this.matchesSearch(location.name));
	}

	/**
	 * Whether a group's header can be expanded/collapsed. A group is expandable when it is
	 * editable, or when it holds at least one editable location the operator can reach even
	 * if the group itself is disabled.
	 *
	 * @param {UserLocationGroup} group
	 * @return {boolean}
	 */
	private isGroupExpandable(group: UserLocationGroup): boolean {
		return group.editable || group.locations.some(location => location.editable);
	}

	/**
	 * Whether a group is expanded. While searching, every group is expanded so
	 * matching locations are visible.
	 *
	 * @param {string} name
	 * @return {boolean}
	 */
	private isGroupExpanded(name: string): boolean {
		return !!this.normalizedSearch || this.expandedGroupNames.includes(name);
	}

	/**
	 * Expand/collapse a group's sub-locations.
	 *
	 * @param {string} name
	 * @return {void}
	 */
	private toggleGroupExpanded(name: string): void {
		this.expandedGroupNames = this.isGroupExpanded(name)
			? this.expandedGroupNames.filter(groupName => groupName !== name)
			: [...this.expandedGroupNames, name];
	}

	/**
	 * Seed the selection location IDs from the location assignments objects, whether
	 * the location is in a group or not.
	 *
	 * @return {void}
	 */
	private seedSelection(): void {
		const selected: Set<string> = new Set();
		this.assignments.forEach(entry => {
			if (entry.type === 'group') {
				entry.locations.forEach(location => {
					if (entry.selected || location.selected) {
						selected.add(location.id);
					}
				});
			}
			else if (entry.selected) {
				selected.add(entry.id);
			}
		});
		this.selectedLocationIds = [...selected];
	}

	/**
	 * Set the state of the group's checkbox.
	 * - All sub-locations selected: checked
	 * - Some selected: indeterminate
	 * - None selected: unchecked
	 *
	 * @param {UserLocationGroup} group
	 * @return {'checked' | 'indeterminate' | 'unchecked'}
	 */
	private groupCheckboxState(group: UserLocationGroup): 'checked' | 'indeterminate' | 'unchecked' {
		const selectedMembers = group.locations.filter(location => this.selectedLocationIdsSet.has(location.id)).length;
		if (selectedMembers === 0) {
			return 'unchecked';
		}
		if (selectedMembers === group.locations.length) {
			return 'checked';
		}
		return 'indeterminate';
	}

	/**
	 * Select/Unselect a location when their checkbox is clicked
	 *
	 * @param {UserLocation} location
	 * @return {void}
	 */
	private toggleLocation(location: UserLocation): void {
		if (!location.editable) {
			return;
		}

		const temp: Set<string> = new Set(this.selectedLocationIds);
		if (temp.has(location.id)) {
			temp.delete(location.id);
		}
		else {
			temp.add(location.id);
		}
		this.selectedLocationIds = [...temp];
		this.$emit('change', this.payload);
	}

	/**
	 * Toggle a group: add/remove only its non-disabled locations depending of its state
	 * - Checked and indeterminate: Unselect non-disabled locations
	 * - Unchecked: select non-disabled locations
	 *
	 * @param {UserLocationGroup} group
	 * @return {void}
	 */
	private toggleGroup(group: UserLocationGroup): void {
		if (!group.editable) {
			return;
		}

		const temp: Set<string> = new Set(this.selectedLocationIds);
		const editableMembers = group.locations.filter(location => location.editable);
		const selectAll = this.groupCheckboxState(group) === 'unchecked';
		editableMembers.forEach(location => {
			if (selectAll) {
				temp.add(location.id);
			}
			else {
				temp.delete(location.id);
			}
		});
		this.selectedLocationIds = [...temp];
		this.$emit('change', this.payload);
	}

	/**
	 * Remove all the non-disabled locations from the selection.
	 *
	 * @return {void}
	 */
	private unselectAll(): void {
		this.selectedLocationIds = this.selectedLocationIds.filter(id => this.disabledLocationIds.has(id));
		this.$emit('change', this.payload);
	}
}
